import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-day-picker/lib/style.css';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector, useStore } from 'react-redux';
import * as Yup from 'yup';
import { AMENITY_PARKING } from '../../../helper/types';
import { clearAmenity, getAmenityV2 } from '../../../redux/actions/amenities';
import {
  clearAmenityAvailabilityV2,
  clearBookingCostBreakdown,
  clearBookingError,
  editBookingV3,
  getBookingsCalendarV3,
  getBookingsListV3
} from '../../../redux/actions/bookingParAm';
import { changeOpenedModal } from '../../../redux/actions/headerModal';
import { setToast } from '../../../redux/actions/window';
import { delay } from 'lodash';
import { useTranslation } from 'react-i18next';
import { WizardModalLayout } from '../../../components/ModalScrollProgress';
import {
  getBookingWizardModalProps,
  StyledModalCardV2
} from '../../../components/modalV2';
import LoadingAvatar from '../../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../../utils/colorCode';
import { GET_AMENITY_AVAILABILITY_V2, GET_BOOKING_COST_BREAKDOWN, GET_BOOKING_V3 } from '../../../redux/constants';
import { requestFail, requestPending, requestSuccess } from '../../../utils/status';
import AddBookingConfirmationStepV2 from './AddBookingConfirmationStepV2';
import AddBookingDetailStepV2 from './AddBookingDetailStepV2';
import { buildListV3Params } from './bookingsFilterHelpers';
import { useBookingAvailabilityFetcher } from '../../properties/Unit&Amenities/Amenities/V2/useBookingAvailabilityFetcher';
import {
  BOOKING_FORM_NO_TIME,
  buildBookingEditFormDefaults,
  buildBookingEditPayload,
  buildBookingValidationSchema
} from './bookingFormHelpers';
import { useBookingDetailForm } from './useBookingDetailForm';
import {
  BOOKING_WIZARD_STEP_COUNT,
  getEditBookingWizardChromeProps
} from './bookingWizardConfigV2';
import WizardChromeV2 from '../../../components/WizardV2/WizardChromeV2';
import {
  WizardFooterButtonPrimary,
  WizardFooterButtonSecondary
} from '../../../components/WizardV2/wizardStylesV2';

const EditBookingV2 = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();

  const [selectedAmenityId, setSelectedAmenityId] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const initializedBookingIdRef = useRef(null);

  const { booking, error, availabilitySlotsV2, status: bookingStatus, bookingCostBreakdown } = useSelector(
    (state) => state.parAmBooking
  );
  const { modal } = useSelector((state) => state.headerModal);
  const { amenities, amenity } = useSelector((state) => state.amenities);

  const availabilitySlots = availabilitySlotsV2;
  const slotsLoading = bookingStatus === requestPending(GET_AMENITY_AVAILABILITY_V2);
  const bookingLoading = bookingStatus === requestPending(GET_BOOKING_V3);
  const bookingFailed = bookingStatus === requestFail(GET_BOOKING_V3);

  const showModal = modal === 'editBookingV2' || modal === 'editBookingV2Loading';
  const showForm = modal === 'editBookingV2' && booking?.id && !bookingLoading && !bookingFailed;
  const requiresPayment = !!amenity?.require_payment;

  const [verifyTimeslot, setVerifyTimeslot] = useState(false);

  const validationSchema = useMemo(
    () =>
      buildBookingValidationSchema({
        yup: Yup,
        t,
        amenity,
        availabilitySlots,
        verifyTimeslot,
        selectedAmenityId
      }),
    [t, verifyTimeslot, availabilitySlots, amenity, selectedAmenityId]
  );

  const schemaRef = useRef(validationSchema);
  schemaRef.current = validationSchema;

  const dynamicYupResolver = useCallback((values, context, options) => {
    return yupResolver(schemaRef.current)(values, context, options);
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    reset,
    setValue,
    resetField,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: dynamicYupResolver,
    mode: 'onChange',
    defaultValues: {
      startTime: BOOKING_FORM_NO_TIME,
      endTime: BOOKING_FORM_NO_TIME,
      endDate: undefined
    }
  });

  const watchAllFields = watch();
  const watchAmenityId = watch('amenity_id');
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');
  const watchStartTime = watch('startTime');
  const watchEndTime = watch('endTime');

  const { lastAvailabilityKeyRef } = useBookingAvailabilityFetcher({
    watchStartDate,
    watchAmenityId: booking?.amenity_id,
    bookingId: booking?.id,
    type: null,
    dispatch,
    active: showForm,
    setVerifyTimeslot
  });

  const {
    allowMultiDayBooking,
    endTimeOptions,
    disabledDates,
    disabledEndDate
  } = useBookingDetailForm({
    amenity,
    availabilitySlots,
    watchStartTime,
    watchEndDate,
    watchStartDate,
    watchEndTime,
    watchAmenityId,
    watchBuilding: null, // Not used in edit mode
    setValue,
    clearErrors,
    bookingId: booking?.id,
    isFormInitialized
  });

  useEffect(() => {
    if (!showForm || !booking?.id) return;
    if (initializedBookingIdRef.current === booking.id) return;

    const defaults = buildBookingEditFormDefaults(booking);
    if (!defaults) return;

    initializedBookingIdRef.current = booking.id;
    reset(defaults);
    setIsFormInitialized(true);
    lastAvailabilityKeyRef.current = null;
    setWizardStep(0);
    setVerifyTimeslot(false);
  }, [showForm, booking, reset]);

  useEffect(() => {
    if (!booking?.amenity_id) return;
    const typeId =
      booking.amenity_type ??
      amenities.find((a) => parseInt(a.id, 10) === parseInt(booking.amenity_id, 10))?.type_id;
    if (typeId != null) setSelectedAmenityId(typeId);
  }, [booking?.amenity_id, booking?.amenity_type, amenities]);

  useEffect(() => {
    if (!showModal) {
      setWizardStep(0);
      lastAvailabilityKeyRef.current = null;
      initializedBookingIdRef.current = null;
      setIsFormInitialized(false);
    }
  }, [showModal]);

  useEffect(() => {
    if (!showForm) return;
    if (watchStartDate) {
      trigger('startDate');
    }
  }, [showForm, watchStartDate, amenity, trigger]);

  useEffect(() => {
    if (
      bookingStatus === requestSuccess(GET_AMENITY_AVAILABILITY_V2) ||
      bookingStatus === requestFail(GET_AMENITY_AVAILABILITY_V2)
    ) {
      setVerifyTimeslot(true);
    }
  }, [bookingStatus]);

  useEffect(() => {
    const availableTimeTest = async () => {
      await trigger(['startDate']);
    };
    if (watchStartDate && verifyTimeslot) {
      delay(availableTimeTest, 500);
    }
  }, [availabilitySlots, verifyTimeslot, trigger, watchStartDate]);

  useEffect(() => {
    if (!showForm || !booking?.amenity_id) return;
    const amenityIdInt = parseInt(booking.amenity_id, 10);
    if (!Number.isFinite(amenityIdInt) || amenityIdInt <= 0) return;

    dispatch(
      getAmenityV2({
        id: amenityIdInt,
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to get amenity!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  }, [showForm, booking?.amenity_id, dispatch, t]);

  const onClose = () => {
    dispatch(clearAmenity());
    dispatch(clearAmenityAvailabilityV2());
    dispatch(clearBookingCostBreakdown());
    dispatch(changeOpenedModal({ modal: '' }));
    dispatch(clearBookingError());
    setWizardStep(0);
    lastAvailabilityKeyRef.current = null;
    initializedBookingIdRef.current = null;
    setIsFormInitialized(false);
  };

  const customSubmit = (values) => {
    if (!booking?.id) return;
    setIsSubmitting(true);

    dispatch(
      editBookingV3({
        id: booking.id,
        body: buildBookingEditPayload(
          values,
          booking,
          selectedAmenityId === AMENITY_PARKING,
          amenity
        ),
        success: () => {
          const latest = store.getState();
          const calendarSnap = latest.parAmBooking.lastBookingsCalendarV3Params;
          const buildingId = latest.building.globalBuildingId;
          const orgId = latest.Organization.globalOrgId;
          onClose();
          if (calendarSnap && Number(calendarSnap.building) === Number(buildingId)) {
            dispatch(getBookingsCalendarV3(calendarSnap));
          }
          dispatch(
            getBookingsListV3(
              buildListV3Params({
                buildingId,
                page: 1,
                search: latest.parAmBooking.search,
                orgId
              })
            )
          );
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Booking details have been successfully updated!')
            })
          );
          setIsSubmitting(false);
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Updation Failed!'),
              modal: 'errorToast'
            })
          );
          setIsSubmitting(false);
        }
      })
    );
  };

  const handleNext = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const fields = ['user', 'startDate'];
    if (allowMultiDayBooking) {
      fields.push('endDate');
    }
    fields.push('startTime', 'endTime');
    if (selectedAmenityId === AMENITY_PARKING) {
      fields.push('make', 'model', 'color', 'plate_number');
    }
    const ok = await trigger(fields, { shouldFocus: true });
    if (ok) setWizardStep(1);
  };

  const onFormSubmit = (e) => {
    if (wizardStep !== 1) {
      e.preventDefault();
      return;
    }
    handleSubmit(customSubmit)(e);
  };

  const safeStep = Math.min(Math.max(0, wizardStep), BOOKING_WIZARD_STEP_COUNT - 1);
  const modalTitle = t('Edit Booking', { defaultValue: 'Edit Booking' });
  const wizardChromeProps = getEditBookingWizardChromeProps(safeStep, t, { modalTitle });
  const costBreakdownLoading =
    requiresPayment && bookingStatus === requestPending(GET_BOOKING_COST_BREAKDOWN);
  const submitDisabled =
    isSubmitting ||
    costBreakdownLoading ||
    (requiresPayment && safeStep === 1 && !bookingCostBreakdown);

  if (!showModal) {
    return null;
  }

  return (
    <form name="EditBookingForm" onSubmit={onFormSubmit}>
      <WizardModalLayout
        Modal={StyledModalCardV2}
        modalProps={getBookingWizardModalProps(safeStep === 1)}
        show={showModal}
        onClose={onClose}
        width="460px"
        bodyGap="12px"
        deps={[wizardStep, showForm, booking?.id]}
        error={error}
        footerActions={
          showForm ? (
            wizardStep === 0 ? (
              <>
                <WizardFooterButtonSecondary type="button" onClick={onClose}>
                  {t('Cancel')}
                </WizardFooterButtonSecondary>
                <WizardFooterButtonPrimary type="button" onClick={handleNext}>
                  {t('Next', { defaultValue: 'Next' })}
                </WizardFooterButtonPrimary>
              </>
            ) : (
              <>
                <WizardFooterButtonSecondary type="button" onClick={() => setWizardStep(0)}>
                  {t('Back')}
                </WizardFooterButtonSecondary>
                <WizardFooterButtonPrimary type="submit" disabled={submitDisabled}>
                  {t('Update Booking')}
                </WizardFooterButtonPrimary>
              </>
            )
          ) : null
        }>
        <WizardChromeV2 {...wizardChromeProps}>
          {bookingLoading || modal === 'editBookingV2Loading' ? (
            <LoadingAvatar color={LOADING_BLUE} />
          ) : bookingFailed ? (
            <div className="error_for_modal p-4">
              <h4 className="text-danger">{error}</h4>
            </div>
          ) : showForm ? (
            <>
              {wizardStep === 0 && (
                <AddBookingDetailStepV2
                  mode="edit"
                  t={t}
                  control={control}
                  errors={errors}
                  watch={watch}
                  resetField={resetField}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  amenity={amenity}
                  disabledDates={disabledDates}
                  disabledEndDate={disabledEndDate}
                  selectedAmenityId={selectedAmenityId}
                  availabilitySlots={availabilitySlots}
                  endTimeOptions={endTimeOptions}
                  watchStartTime={watchStartTime}
                  watchStartDate={watchStartDate}
                  watchAllFields={watchAllFields}
                  slotsLoading={slotsLoading}
                  allowMultiDayBooking={allowMultiDayBooking}
                  amenityDisplayName={
                    booking?.amenity_name ||
                    booking?.title ||
                    amenity?.description ||
                    amenity?.code ||
                    amenities.find(
                      (a) => parseInt(a.id, 10) === parseInt(booking?.amenity_id, 10)
                    )?.description
                  }
                />
              )}

              {wizardStep === 1 && (
                <AddBookingConfirmationStepV2
                  t={t}
                  getValues={getValues}
                  amenity={amenity}
                />
              )}
            </>
          ) : null}
        </WizardChromeV2>
      </WizardModalLayout>
    </form>
  );
};

export default EditBookingV2;
